import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getLocationsByPostcode, getLocationById } from '@/app/actions';
import debounce from 'lodash.debounce';
import { Location24Regular } from '@fluentui/react-icons';
import { RecordModel } from 'pocketbase';

const SearchSelectLocation: React.FC<{ setLocation: (location: RecordModel) => void }> = ({ setLocation }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [displayValue, setDisplayValue] = useState('');
  const [suggestions, setSuggestions] = useState<RecordModel[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [initialLocationName, setInitialLocationName] = useState('');

  const { data: postcodeData, error: postcodeError, isLoading: isPostcodeLoading } = useQuery({
    queryKey: ['locationsByPostcode', searchTerm],
    queryFn: () => getLocationsByPostcode(searchTerm),
    enabled: searchTerm.length > 2,
  });

  useEffect(() => {
    if (postcodeData && Array.isArray(postcodeData)) {
      setSuggestions(postcodeData);
    }
  }, [postcodeData]);

  const debouncedSearchChange = debounce((value: string) => {
    setSearchTerm(value);
  }, 300);

  const handleSearchChange = (value: string) => {
    setDisplayValue(value);
    debouncedSearchChange(value);
  };

  const handleLocationSelect = (selectedLocation: RecordModel) => {
    setLocation(selectedLocation);
    setDisplayValue(selectedLocation.locationName);
    setInitialLocationName(selectedLocation.locationName);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
      if (!suggestions.find(suggestion => suggestion.locationName === displayValue)) {
        setDisplayValue(initialLocationName);
      }
    }, 300);
  };

  return (
    <div className="flex flex-col relative flex-grow">
      <div className="relative w-full">
        <input
          type="text"
          placeholder="Search for location"
          value={displayValue}
          onChange={(e) => handleSearchChange(e.target.value)}
          onFocus={() => setShowSuggestions(true)}
          onBlur={handleBlur}
          className="input input-bordered w-full border-primary pr-10"
        />
        <Location24Regular className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none" />
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <ul className="absolute bg-white border border-gray-300 w-full max-h-40 overflow-y-auto z-10 mt-1 top-12">
          {suggestions.map((suggestion) => (
            <li
              key={suggestion.id}
              className="p-2 cursor-pointer hover:bg-gray-200"
              onMouseDown={() => handleLocationSelect(suggestion)}
            >
              {suggestion.locationName +
                `${suggestions.filter((x) => x.locationName === suggestion.locationName).length > 1
                  ? ` (${suggestion.postcode})`
                  : ``
                }`}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SearchSelectLocation;